import * as React from 'react';
import Stack from '@mui/material/Stack';

import { EventStatusEnum, GetEventDetailedQuery, GetSpotDetailedQuery, GetSpotBookingQuery } from '@graphql';
import { Header, HeaderProps } from './Header';
import { Body, BodyProps } from './Body';
import { Footer, FooterProps } from './Footer';
import zIndex from '@mui/material/styles/zIndex';

const ManagedFormEnum = {
  Cover: 'cover',
  Preview: 'preview',
  Description: 'description',
  Participant: 'participant',
  Ticket: 'ticket',
  Spot: 'spot',
  Address: 'address',
  Faq: 'faq',
  Tag: 'tag',
} as const;

type ManagedFormType = (typeof ManagedFormEnum)[keyof typeof ManagedFormEnum];

interface WrapperProps {
  type: ManagedFormType;
  isOwner?: boolean;
  children: React.ReactNode;
}

interface DialogEditWrapperProps extends Omit<WrapperProps, 'type'> {
  entity: 'event' | 'spot' | 'booking';
  event?: GetEventDetailedQuery['event'] | null;
  spot?: GetSpotDetailedQuery['spot'] | null;
  booking?: GetSpotBookingQuery['booking'] | null;
}

interface DialogEditContextValue {
  entity: 'event' | 'spot' | 'booking';
  event?: GetEventDetailedQuery['event'] | null;
  spot?: GetSpotDetailedQuery['spot'] | null;
  booking?: GetSpotBookingQuery['booking'] | null;
  isOwner: boolean;
  isManaging: ManagedFormType | null;
  setIsManaging: React.Dispatch<React.SetStateAction<ManagedFormType | null>>;
  isEditMode: boolean;
  setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
}

export const DialogEditContext = React.createContext<DialogEditContextValue | null>(null);

export const DialogEditWrapper = (props: Readonly<DialogEditWrapperProps>) => {
  const { entity, event, spot, booking, isOwner = false, children } = props;
  const [isManaging, setIsManaging] = React.useState<ManagedFormType | null>(null);
  const [isEditMode, setIsEditMode] = React.useState(
    Boolean(event && event.status === EventStatusEnum.Draft)
  );

  if (entity === 'event' && !event) {
    throw new Error('DialogEditWrapper: event data is required when entity is "event"');
  }

  if (entity === 'spot' && !spot) {
    throw new Error('DialogEditWrapper: spot data is required when entity is "spot"');
  }

  if (entity === 'booking' && !booking) {
    throw new Error('DialogEditWrapper: booking data is required when entity is "booking"');
  }

  return (
    <DialogEditContext.Provider
      value={{ entity, event, spot, booking, isOwner, isManaging, setIsManaging, isEditMode, setIsEditMode }}
    >
      {children}
    </DialogEditContext.Provider>
  );
};

interface WrapperContextValue extends DialogEditContextValue {
  isEditMode: boolean;
  setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  managingType: ManagedFormType;
  isSaving: boolean;
  setIsSaving: React.Dispatch<React.SetStateAction<boolean>>;
  isSaveDisabled: boolean;
  setIsSaveDisabled: React.Dispatch<React.SetStateAction<boolean>>;
}

export const WrapperContext = React.createContext<WrapperContextValue | null>(null);

export const Wrapper = (props: Readonly<WrapperProps>) => {
  const context = React.useContext(DialogEditContext);

  if (!context) {
    throw new Error('Wrapper must be used inside DialogEditWrapper');
  }

  const { type: managingType, children } = props;
  const [isSaving, setIsSaving] = React.useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = React.useState(true);
  const isEditing = context.isManaging !== null && context.isManaging === managingType;

  const sx = {
    zIndex: isEditing ? zIndex.modal + 1 : undefined,
  };

  return (
    <WrapperContext.Provider
      value={{ ...context, managingType, isSaving, setIsSaving, isSaveDisabled, setIsSaveDisabled }}
    >
      <Stack spacing={2} position="relative" height="100%" justifyContent="space-between" sx={sx}>
        {children}
      </Stack>
    </WrapperContext.Provider>
  );
};

Wrapper.Header = (props: HeaderProps) => <Header {...props} />;
Wrapper.Body = (props: BodyProps) => <Body {...props} />;
Wrapper.Footer = (props: FooterProps) => <Footer {...props} />;
